import { font, themedColor, useClickOutside } from '@lemonade-hq/cdk';
import { FatSmallBottomChevronIcon, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { PricingWidget } from './PricingWidget';

const BASE_URL = '/investor';

export const LINKS = [
  { label: 'Feed', url: '' },
  { label: 'Quarterly Results', url: '/quarterly-results' },
  { label: 'News', url: '/news' },
  { label: 'Events', url: '/events' },
  { label: 'Stock Info', url: '/stock-information' },
  { label: 'Filings', url: '/filings' },
  { label: 'Governance', url: '/governance' },
  { label: 'Founders’ Letter', url: '/founders-letter' },
  { label: 'FAQs', url: '/faq' },
];

const StyledLink = styled(Link)<{ isActive: boolean }>`
  ${font('main', { fontSize: '1.6rem' })};
  text-decoration: none;
  padding-block: 16px;
  display: block;
  color: ${({ theme }) => theme.colors.mouseGray};
  ${({ isActive, theme }) => isActive && `color: ${theme.colors.primary}; font-weight: 700;`}
  padding-inline: 20px;
  text-decoration: none !important;

  ${MEDIA_QUERIES.tabletAndDesktop`
    padding-block: 10px;
    padding-inline: 0;
    border-bottom: none;
    color: ${({ theme }) => theme.colors.mouseGray};
    &:hover {
      color: ${({ theme }) => theme.colors.textPrimary};
      text-decoration: none;
    }
    ${({ isActive, theme }) =>
      isActive &&
      `
      color: ${theme.colors.primary};
      font-weight: 700;

      &:hover {
        color: ${theme.colors.primary};
    `}
  `}
`;

const StyledMenu = styled.ul`
  display: flex;
  flex-direction: column;
  background-color: white;
  position: fixed;
  top: 140px;
  width: calc(100% - 40px);
  left: 20px;
  z-index: 999;
  border: solid 1px ${themedColor('glass')};
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: flex;
    position: relative;
    top:0;
    left: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
  `}

  ${MEDIA_QUERIES.desktop`
    width: 160px;
  `}

  ${MEDIA_QUERIES.tablet`
    width: 138px;
  `}
`;

const LinksWrapperMobile = styled.nav`
  display: block;
  position: fixed;
  left: 0;
  top: 69px;
  width: 100%;
  background-color: white;
  z-index: 99;
  border-bottom: 1px solid ${themedColor('concrete')};
  padding-inline: 20px;
  padding-block: 15px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}
`;

const LinksWrapperDesktop = styled.aside`
  position: sticky;
  top: 140px;
  width: 198px;
  height: auto;
  padding: 0 0 0 20px;
  display: block;

  ${MEDIA_QUERIES.mobile`
    display: none;
  `};
`;

const Links: React.FC = () => {
  const router = useRouter();

  return (
    <StyledMenu>
      {LINKS.map(link => {
        const url = `${BASE_URL}${link.url}`;

        const isActive = url === BASE_URL ? url === router.pathname : router.pathname.includes(url);
        return (
          <li key={url}>
            <StyledLink isActive={isActive} href={url}>
              {link.label}
            </StyledLink>
          </li>
        );
      })}
    </StyledMenu>
  );
};

const MobileSelector = styled.button`
  ${font('main', {
    fontSize: '1.6rem',
    lineHeight: '1.6rem',
  })};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: none;
  height: 52px;
  border: solid 1px ${({ theme }) => theme.colors.glass};
  cursor: pointer;
  text-align: left;
  width: 100%;
  padding: 0;
  margin: 0;
  outline: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  border-radius: 10px;
  position: relative;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none;
  `}
`;

const Icon = styled(FatSmallBottomChevronIcon)<{ open: boolean }>`
  position: absolute;
  top: 50%;
  right: 15px;
  pointer-events: none;
  transition: transform 0.2s ease-in-out;
  transform: translateY(-50%) ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const MobileMenuSelector: React.FC<{ open: boolean; setOpen: (open: boolean) => void }> = ({ open, setOpen }) => {
  const router = useRouter();

  const value = LINKS.find(({ url }) =>
    url === '' ? `${BASE_URL}${url}` === router.pathname : router.pathname.includes(url),
  )?.label;

  return (
    <MobileSelector onClick={() => setOpen(!open)}>
      {value}
      <Icon open={open} />
    </MobileSelector>
  );
};

export const Sidebar = () => {
  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLUListElement>(null);
  useClickOutside(ref, open, () => setOpen(false));

  return (
    <>
      <LinksWrapperMobile ref={ref}>
        <MobileMenuSelector open={open} setOpen={setOpen} />
        {open && <Links />}
      </LinksWrapperMobile>
      <LinksWrapperDesktop>
        <Links />
        <PricingWidget />
      </LinksWrapperDesktop>
    </>
  );
};
